/**
 * One AI call as five stacked layers — input, context, reasoning, answer,
 * checks — so the layer where it drifted reads straight off the page.
 *
 * Every flag comes from what the call recorded (its failed step and warning
 * notes), except the grounding check, which is a judge model's estimate and is
 * labelled as one. The reasoning layer shows the text the provider returned,
 * exactly as returned — nothing is inferred about what the model did not write.
 */
import { useState } from 'react';

import { aiObservabilityService } from '../../services/ai-observability.service';
import {
  fmtCost, fmtTokens,
  type ObsGrounding, type ObsLayer, type ObsLayerName, type ObsLayerStatus, type ObsLayers,
  type ObsRetrievedPassage,
} from '../../types/ai-observability.types';
import { ICONS } from './obs-kit';
import { CardHead } from './obs-shell';

export const LAYER_INFO: Record<ObsLayerName, { title: string; what: string }> = {
  input: { title: 'Input', what: 'What the model was asked.' },
  context: { title: 'Context', what: 'What it was given to answer from.' },
  reasoning: { title: 'Reasoning', what: 'The thinking the model returned, as returned.' },
  answer: { title: 'Answer', what: 'What it said.' },
  checks: { title: 'Checks', what: 'Citations, and whether the answer is supported.' },
};

const STATUS: Record<ObsLayerStatus, { label: string; dot: string; chip?: React.CSSProperties }> = {
  ok: { label: 'ok', dot: '#22C55E' },
  warn: { label: 'warning', dot: '#F59E0B',
    chip: { background: '#FEF3C7', borderColor: '#FDE68A', color: '#92400E' } },
  fail: { label: 'failed', dot: '#EF4444',
    chip: { background: '#FEE2E2', borderColor: '#FECACA', color: '#991B1B' } },
  na: { label: 'n/a', dot: '#CBD5E1' },
  hidden: { label: 'not captured', dot: '#CBD5E1' },
};

const LABEL: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: 'var(--obs-ink-3)', margin: '10px 0 5px',
  textTransform: 'uppercase', letterSpacing: '.04em',
};

const BLOCK: React.CSSProperties = {
  margin: 0, padding: '9px 11px', background: 'var(--obs-sunken)',
  border: '1px solid var(--obs-line)', borderRadius: 6, whiteSpace: 'pre-wrap',
  wordBreak: 'break-word', fontSize: 12.5, lineHeight: 1.6, maxHeight: 360, overflowY: 'auto',
};

/** A long text, in its own paragraphs, so a model's reasoning scans step by step. */
function Text({ value, mono }: { value: string; mono?: boolean }) {
  const paragraphs = value.split(/\n\s*\n/).filter((p) => p.trim());
  if (mono || paragraphs.length < 2) {
    return <pre style={{ ...BLOCK, fontFamily: mono ? 'ui-monospace, "SF Mono", Menlo, monospace' : 'inherit' }}>{value}</pre>;
  }
  return (
    <ol style={{ ...BLOCK, paddingLeft: 30, whiteSpace: 'normal' }}>
      {paragraphs.map((p, i) => (
        <li key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: i < paragraphs.length - 1 ? 8 : 0 }}>{p.trim()}</li>
      ))}
    </ol>
  );
}

/** A retrieved passage is cited when the answer pointed at its source and page. */
function Passage({ p, cited }: { p: ObsRetrievedPassage; cited: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--obs-line-soft)', padding: '8px 0' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        all: 'unset', cursor: 'pointer', display: 'grid', width: '100%', alignItems: 'center',
        gridTemplateColumns: '28px 1fr 60px 70px', gap: 10,
      }} aria-expanded={open}>
        <span className="obs-mono" style={{ color: 'var(--obs-ink-4)' }}>#{p.rank ?? '—'}</span>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', fontSize: 12.5 }} title={p.source ?? undefined}>
          {p.source ?? '—'}{p.page != null ? ` · p.${p.page}` : ''}
        </span>
        <span className="num" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
          {p.score != null ? p.score.toFixed(3) : '—'}
        </span>
        <span className="obs-chip" style={cited
          ? { background: '#F0FDF4', borderColor: '#BBF7D0', color: '#166534' }
          : { color: 'var(--obs-ink-4)' }}>
          {cited ? 'cited' : 'unused'}
        </span>
      </button>
      {open && p.content && <div style={{ margin: '8px 0 0 38px' }}><Text value={p.content} mono /></div>}
    </div>
  );
}

function Verdict({ label, v }: { label: string; v?: { score: number | null; reason: string | null; error: string | null } }) {
  if (!v) return null;
  const low = v.score != null && v.score < 0.7;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '6px 0' }}>
      <span style={{ fontSize: 12.5, fontWeight: 500, width: 80, flex: 'none' }}>{label}</span>
      <span className="obs-chip" style={low ? STATUS.warn.chip : undefined}>
        {v.score != null ? `${Math.round(v.score * 100)}% supported` : 'not scored'}
      </span>
      <span className="ea-sub" style={{ fontSize: 12 }}>{v.reason ?? v.error ?? ''}</span>
    </div>
  );
}

function Grounding({ id, value, can, onStored }: { id: string; value?: ObsGrounding; can: boolean; onStored?: () => void }) {
  const [g, setG] = useState<ObsGrounding | undefined>(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const result = await aiObservabilityService.checkGrounding(id);
      setG(result);
      // Saved: reload so the layers it judged pick up their flags.
      if (result.stored) onStored?.();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErr(msg ?? (e instanceof Error ? e.message : 'Could not run the check.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8 }}>
        Grounding <span style={{ textTransform: 'none', fontWeight: 400 }}>— estimate by a judge model</span>
      </div>
      {g ? (
        <>
          <Verdict label="Reasoning" v={g.reasoning} />
          <Verdict label="Answer" v={g.answer} />
          <div className="ea-sub" style={{ fontSize: 11.5, marginTop: 4 }}>
            Judged by {g.judgeModel ?? '—'} against the {g.against}
            {g.cost != null ? ` · ${fmtCost(g.cost, 'USD')}` : ''}. An estimate, not a measurement.
            {g.stored === false && ' Not saved — the database refused the write, so it will be gone on reload.'}
          </div>
        </>
      ) : (
        <div className="ea-sub" style={{ fontSize: 12 }}>
          Not checked. Asks a model how much of the reasoning and answer the {''}
          context supports — one or two model calls.
        </div>
      )}
      {can && (
        <button className="obs-btn" disabled={busy} onClick={run} style={{ padding: '5px 12px', marginTop: 8 }}>
          {busy ? 'Checking…' : g ? 'Check again' : 'Check grounding'}
        </button>
      )}
      {err && <div style={{ fontSize: 12, color: '#991B1B', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function LayerBody({ name, l, id, can, onStored }: {
  name: ObsLayerName; l: ObsLayer; id: string; can: boolean; onStored?: () => void;
}) {
  if (name === 'input') {
    return (
      <>
        {l.question != null && <><div style={LABEL}>Question</div><Text value={l.question} /></>}
        {(l.prompt || l.ragCode) && l.question != null && (
          <div className="ea-sub" style={{ fontSize: 12, marginTop: 6 }}>
            {l.ragCode ? `${l.ragCode}${l.version != null ? ` v${l.version}` : ''}` : ''}
            {l.prompt ? ` · prompt “${l.prompt}”` : ''}
          </div>
        )}
        {l.system && <><div style={LABEL}>System prompt</div><Text value={l.system} mono /></>}
        {l.prompt && l.question == null && <><div style={LABEL}>Rendered prompt</div><Text value={l.prompt} mono /></>}
        {l.variables && Object.keys(l.variables).length > 0 && (
          <><div style={LABEL}>Variables</div><Text value={JSON.stringify(l.variables, null, 2)} mono /></>
        )}
      </>
    );
  }
  if (name === 'context') {
    const cited = new Set((l.citations ?? []).map((c) => `${c.source}|${c.page ?? ''}`));
    if (!l.passages?.length) return null;
    return (
      <>
        <div style={LABEL}>
          {l.passages.length} passage{l.passages.length === 1 ? '' : 's'} by rank
          {l.chunkCount != null ? ` · ${l.chunkCount} chunks searched` : ''}
        </div>
        {l.passages.map((p, i) => <Passage key={i} p={p} cited={cited.has(`${p.source}|${p.page ?? ''}`)} />)}
      </>
    );
  }
  if (name === 'reasoning') {
    return (
      <>
        {l.text && <Text value={l.text} />}
        {l.tokens != null && (
          <div className="ea-sub" style={{ fontSize: 12, marginTop: 6 }}>{fmtTokens(l.tokens)} thinking tokens</div>
        )}
      </>
    );
  }
  if (name === 'answer') {
    return (
      <>
        {l.text && <Text value={l.text} />}
        {l.model && <div className="ea-sub" style={{ fontSize: 12, marginTop: 6 }}>by {l.model}</div>}
      </>
    );
  }
  return (
    <>
      {!!l.citations?.length && (
        <div className="ea-sub" style={{ fontSize: 12.5 }}>
          Cites {l.citations.map((c) => `[${c.index}] ${c.source ?? ''}${c.page != null ? ` p.${c.page}` : ''}`).join(', ')}
        </div>
      )}
      <Grounding id={id} value={l.grounding} can={can} onStored={onStored} />
    </>
  );
}

export function Layers({ id, data, onChange }: { id: string; data: ObsLayers; onChange?: () => void }) {
  // Context passages are marked cited from the checks layer's citations.
  const withCitations = (name: ObsLayerName): ObsLayer =>
    name === 'context' ? { ...data.layers.context, citations: data.layers.checks.citations } : data.layers[name];
  const first = data.firstProblem;

  return (
    <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
      <CardHead icon={ICONS.layers} title="Layers"
        sub="The call from what it was asked to what it said. The first layer marked is where it went wrong." />
      <div style={{ margin: '4px 0 12px', padding: '9px 12px', borderRadius: 6, fontSize: 13,
        ...(first ? STATUS[data.layers[first].status].chip : { background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }),
        borderWidth: 1, borderStyle: 'solid' }}>
        {first
          ? <>First problem at <b>{LAYER_INFO[first].title}</b>: {data.layers[first].flags[0]?.message}</>
          : 'No layer flagged a problem.'}
      </div>

      {data.order.map((name, i) => {
        const l = withCitations(name);
        const st = STATUS[l.status];
        const last = i === data.order.length - 1;
        return (
          <div key={name} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 12 }}>
            {/* The rail: one dot per layer, joined, so the order reads as a path. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: st.dot, marginTop: 14,
                boxShadow: name === first ? `0 0 0 4px ${st.dot}33` : undefined }} />
              {!last && <span style={{ flex: 1, width: 2, background: 'var(--obs-line)', marginTop: 4 }} />}
            </div>
            <details open={l.status === 'fail' || l.status === 'warn' || name === 'reasoning' || name === 'answer'}
              style={{ borderBottom: last ? undefined : '1px solid var(--obs-line-soft)', padding: '8px 0 10px', minWidth: 0 }}>
              <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, listStyle: 'revert' }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{i + 1}. {LAYER_INFO[name].title}</span>
                <span className="obs-chip" style={st.chip}>{st.label}</span>
                <span className="ea-sub" style={{ fontSize: 12 }}>{LAYER_INFO[name].what}</span>
              </summary>
              <div style={{ paddingTop: 6 }}>
                {l.flags.map((f, k) => (
                  <div key={k} style={{ fontSize: 12.5, lineHeight: 1.5, padding: '6px 10px', borderRadius: 6, margin: '4px 0',
                    ...(f.level === 'fail' ? STATUS.fail.chip : STATUS.warn.chip), borderWidth: 1, borderStyle: 'solid' }}>
                    <b style={{ fontWeight: 600 }}>{f.name.replace(/_/g, ' ')}</b> — {f.message}
                  </div>
                ))}
                {l.note && <div className="ea-sub" style={{ fontSize: 12.5, margin: '4px 0' }}>{l.note}</div>}
                <LayerBody name={name} l={l} id={id} can={data.canCheckGrounding} onStored={onChange} />
              </div>
            </details>
          </div>
        );
      })}
    </section>
  );
}
